import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import ExerciseCard from './ExerciseCard';
import SupersetGroup from './SupersetGroup';

const PlanBuilder = ({ 
  planData, 
  onPlanUpdate, 
  onSave, 
  onExerciseUpdate, 
  onExerciseRemove,
  onSupersetCreate,
  onSupersetUpdate 
}) => {
  const [draggedOver, setDraggedOver] = useState(null);
  const dropZoneRef = useRef(null);

  const phaseOptions = [
    { value: 'phase-1', label: 'Phase 1 (2-4 reps)' },
    { value: 'phase-2', label: 'Phase 2 (6-8 reps)' },
    { value: 'phase-3', label: 'Phase 3 (10-12 reps)' }
  ];

  const clientOptions = [
    { value: 'client-001', label: 'Margaret Chen' },
    { value: 'client-002', label: 'Robert Williams' },
    { value: 'client-003', label: 'Dorothy Martinez' },
    { value: 'client-004', label: 'James Thompson' },
    { value: 'client-005', label: 'Mary Johnson' }
  ];

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDraggedOver('main');
  };

  const handleDragLeave = (e) => {
    if (!dropZoneRef?.current?.contains(e?.relatedTarget)) {
      setDraggedOver(null);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDraggedOver(null);
    
    try {
      const exerciseData = JSON.parse(e?.dataTransfer?.getData('application/json'));
      
      const newExercise = {
        id: `plan-ex-${Date.now()}`,
        exerciseId: exerciseData?.id,
        name: exerciseData?.name,
        category: exerciseData?.category,
        type: exerciseData?.type,
        equipment: exerciseData?.equipment,
        muscleGroups: exerciseData?.muscleGroups,
        image: exerciseData?.image,
        description: exerciseData?.description,
        phases: exerciseData?.phases,
        planConfig: {
          selectedPhase: 'phase-1',
          startingIntensity: exerciseData?.phases?.['phase-1']?.startingIntensity,
          supersetGroup: null,
          supersetOrder: null,
          notes: '',
          customAdjustment: false
        },
        order: planData?.exercises?.length
      };

      onPlanUpdate({
        ...planData,
        exercises: [...planData?.exercises, newExercise]
      });
    } catch (error) {
      console.error('Error adding exercise:', error);
    }
  };

  const handlePlanMetaUpdate = (field, value) => {
    onPlanUpdate({
      ...planData,
      [field]: value
    });
  };

  const moveExercise = (fromIndex, toIndex) => {
    const updatedExercises = [...planData?.exercises];
    const [movedExercise] = updatedExercises?.splice(fromIndex, 1);
    updatedExercises?.splice(toIndex, 0, movedExercise);
    
    // Update order values
    updatedExercises?.forEach((exercise, index) => {
      exercise.order = index;
    });

    onPlanUpdate({
      ...planData,
      exercises: updatedExercises
    });
  };

  const groupedExercises = planData?.exercises?.reduce((groups, exercise) => {
    const supersetGroup = exercise?.planConfig?.supersetGroup;
    if (supersetGroup) {
      if (!groups?.[supersetGroup]) {
        groups[supersetGroup] = [];
      }
      groups?.[supersetGroup]?.push(exercise);
    } else {
      if (!groups?.individual) {
        groups.individual = [];
      }
      groups?.individual?.push(exercise);
    }
    return groups;
  }, {});

  // Sort superset exercises by order
  Object.keys(groupedExercises)?.forEach(key => {
    if (key !== 'individual') {
      groupedExercises?.[key]?.sort((a, b) => (a?.planConfig?.supersetOrder || 0) - (b?.planConfig?.supersetOrder || 0));
    }
  });

  const validatePlan = () => {
    const errors = [];
    
    if (!planData?.name?.trim()) {
      errors?.push('Plan name is required');
    }
    
    if (planData?.exercises?.length === 0) {
      errors?.push('At least one exercise is required');
    }

    // Validate superset groups
    Object.keys(groupedExercises)?.forEach(key => {
      if (key !== 'individual' && groupedExercises?.[key]?.length < 2) {
        errors?.push(`Superset ${key} must have at least 2 exercises`);
      }
    });

    return errors;
  };

  const handleSave = () => {
    const errors = validatePlan();
    if (errors?.length > 0) {
      alert('Please fix the following errors:\n' + errors?.join('\n'));
      return;
    }
    
    onSave(planData);
  };

  return (
    <div className="h-full flex flex-col bg-surface border border-border rounded-lg">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Training Plan Builder</h2>
            <p className="text-sm text-muted-foreground">
              {planData?.exercises?.length} exercises • {Object.keys(groupedExercises)?.filter(k => k !== 'individual')?.length} supersets
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => onPlanUpdate({ ...planData, exercises: [] })}
              iconName="Trash2"
              className="breathing-focus"
            >
              Clear All
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              iconName="Save"
              className="breathing-focus haptic-button"
            >
              Save Plan
            </Button>
          </div>
        </div>

        {/* Plan Metadata */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Input
            label="Plan Name"
            type="text"
            placeholder="Enter training plan name"
            value={planData?.name}
            onChange={(e) => handlePlanMetaUpdate('name', e?.target?.value)}
            required
          />
          
          <Select
            label="Assign to Client"
            options={clientOptions}
            value={planData?.assignedClient}
            onChange={(value) => handlePlanMetaUpdate('assignedClient', value)}
            placeholder="Select client (optional)"
          />
          
          <div className="lg:col-span-2">
            <Input
              label="Description"
              type="text"
              placeholder="Brief description of the training plan"
              value={planData?.description}
              onChange={(e) => handlePlanMetaUpdate('description', e?.target?.value)}
            />
          </div>
          
          <Select
            label="Default Phase"
            options={phaseOptions}
            value={planData?.defaultPhase}
            onChange={(value) => handlePlanMetaUpdate('defaultPhase', value)}
          />
          
          <div className="flex items-center space-x-4 pt-6">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={planData?.autoProgression}
                onChange={(e) => handlePlanMetaUpdate('autoProgression', e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <span className="text-sm text-foreground">Auto progression</span>
            </label>
            
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={planData?.adaptiveIntensity}
                onChange={(e) => handlePlanMetaUpdate('adaptiveIntensity', e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <span className="text-sm text-foreground">Adaptive intensity</span>
            </label>
          </div>
        </div>
      </div>
      {/* Plan Content */}
      <div className="flex-1 overflow-y-auto">
        {planData?.exercises?.length === 0 ? (
          <div
            ref={dropZoneRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              m-6 p-12 border-2 border-dashed rounded-lg text-center
              transition-all duration-200 ease-out
              ${draggedOver === 'main' ?'border-primary bg-primary/5' :'border-border bg-muted/30'
              }
            `}
          >
            <Icon 
              name="Plus" 
              size={48} 
              className={`mx-auto mb-4 ${draggedOver === 'main' ? 'text-primary' : 'text-muted-foreground'}`} 
            />
            <h3 className="text-lg font-medium text-foreground mb-2">
              Start Building Your Training Plan
            </h3>
            <p className="text-muted-foreground mb-4">
              Drag exercises from the database to add them to your plan
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="MousePointer2" size={16} />
                <span>Drag & Drop</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Link" size={16} />
                <span>Create Supersets</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Settings" size={16} />
                <span>Configure Phases</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Individual Exercises */}
            {groupedExercises?.individual?.map((exercise, index) => (
              <ExerciseCard
                key={exercise?.id}
                exercise={exercise}
                index={index}
                onUpdate={onExerciseUpdate}
                onRemove={onExerciseRemove}
                onMove={moveExercise}
                onSupersetCreate={onSupersetCreate}
                phaseOptions={phaseOptions}
              />
            ))}

            {/* Superset Groups */}
            {Object.keys(groupedExercises)?.filter(key => key !== 'individual')?.map(supersetId => (
                <SupersetGroup
                  key={supersetId}
                  supersetId={supersetId}
                  exercises={groupedExercises?.[supersetId]}
                  onUpdate={onSupersetUpdate}
                  onExerciseUpdate={onExerciseUpdate}
                  onExerciseRemove={onExerciseRemove}
                  phaseOptions={phaseOptions}
                />
              ))}

            {/* Drop Zone for Additional Exercises */}
            <div
              ref={dropZoneRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                p-8 border-2 border-dashed rounded-lg text-center
                transition-all duration-200 ease-out
                ${draggedOver === 'main' ?'border-primary bg-primary/5' :'border-border bg-muted/30'
                }
              `}
            >
              <Icon 
                name="Plus" 
                size={32} 
                className={`mx-auto mb-2 ${draggedOver === 'main' ? 'text-primary' : 'text-muted-foreground'}`} 
              />
              <p className="text-sm text-muted-foreground">
                Drop more exercises here to add them to your plan
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center space-x-4">
            <span>Plan Progress: {planData?.exercises?.length} exercises</span>
            {planData?.exercises?.length > 0 && (
              <span>Estimated Duration: {Math.ceil(planData?.exercises?.length * 2.5)} minutes</span>
            )}
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-primary rounded-full"></div>
              <span>Individual</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-secondary rounded-full"></div>
              <span>Superset</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanBuilder;